import React from 'react';
import {
  IdentificationIcon,
  MapPinIcon,
  PhoneIcon,
  CalendarIcon,
  BuildingLibraryIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { Card } from '../ui';

const Row: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
  span?: boolean;
}> = ({ icon: Icon, label, children, span }) => (
  <div className={clsx('flex gap-3 rounded-xl border border-slate-100 bg-slate-50/40 p-3', span && 'md:col-span-2')}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-900">{children}</p>
    </div>
  </div>
);

export interface ClientPersonalInfoCardProps {
  client: Client;
  className?: string;
}

export const ClientPersonalInfoCard: React.FC<ClientPersonalInfoCardProps> = ({ client, className }) => {
  const dob = client.dateOfBirth
    ? new Date(client.dateOfBirth).toLocaleDateString('es', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '—';

  return (
    <Card className={clsx('overflow-hidden border-slate-200/90 p-0 shadow-sm ring-1 ring-slate-100/80', className)}>
      <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <h3 className="text-base font-bold text-slate-900">Información personal</h3>
        <p className="mt-0.5 text-xs text-slate-500">Datos de identificación y contacto del titular</p>
      </div>
      <div className="grid gap-3 p-5 md:grid-cols-2">
        <Row icon={TagIcon} label="Carnet">
          {client.carnetNo || '—'}
        </Row>
        <Row icon={IdentificationIcon} label="Categoría">
          {client.categoria || '—'}
        </Row>
        <Row icon={BuildingLibraryIcon} label="Gremio">
          {(client.gremio || '').trim() || 'Sin gremio'}
        </Row>
        <Row icon={CalendarIcon} label="Fecha de nacimiento">
          {dob}
        </Row>
        <Row icon={IdentificationIcon} label="Edad">
          {client.age != null ? `${client.age} años` : '—'}
        </Row>
        <Row icon={PhoneIcon} label="Teléfonos">
          {client.telefonos?.trim() || '—'}
        </Row>
        <Row icon={MapPinIcon} label="Dirección" span>
          {client.address}
        </Row>
        {client.nacionalidad && (
          <Row icon={TagIcon} label="Nacionalidad">
            {client.nacionalidad}
          </Row>
        )}
      </div>
    </Card>
  );
};
